import { useState } from "react";
import { X, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { track } from "@/lib/analytics";


interface BookCTAProps {
  className?: string;
  variant?: "primary" | "card" | "link";
  size?: "sm" | "md" | "lg";
  children?: React.ReactNode;
  source?: string;
}

const BookCTA = ({
  className = "",
  variant = "primary",
  size = "lg",
  children = "Get the Book →",
  source = "unknown",
}: BookCTAProps) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");

  const isLink = variant === "link";
  const baseBtn =
    variant === "primary"
      ? "bg-primary text-primary-foreground"
      : "bg-card text-foreground";
  const sizeCls =
    size === "lg" ? "px-8 py-3 text-lg" :
    size === "sm" ? "px-3 py-1.5 text-xs" :
    "px-5 py-2 text-sm";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    const { data, error } = await supabase.functions.invoke("beehiiv-subscribe", {
      body: {
        email,
        utm_source: "book_cta",
        utm_medium: "button",
        send_welcome_email: true,
        welcome_template: "book_cta",
      },
    });
    if (error || (data as any)?.error) {
      console.error("book cta error", error, data);
      setStatus("error");
      return;
    }
    track("book_subscribed", { utm_source: "book_cta" });
    setStatus("success");
  };

  const close = () => {
    setOpen(false);
    setTimeout(() => {
      setEmail("");
      setStatus("idle");
    }, 200);
  };

  const handleOpen = () => {
    track("book_cta_click", { source });
    setOpen(true);
  };

  return (
    <>
      {isLink ? (
        <button
          type="button"
          onClick={handleOpen}
          className={`font-bold text-primary hover:underline uppercase tracking-[0.08em] text-sm ${className}`}
        >
          {children}
        </button>
      ) : (
        <button
          type="button"
          onClick={handleOpen}
          className={`brutal-border brutal-shadow-lg ${baseBtn} font-bold ${sizeCls} hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all rounded-md uppercase tracking-[0.08em] ${className}`}
        >
          {children}
        </button>
      )}

      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-foreground/40 backdrop-blur-sm"
          onClick={close}
        >
          <div
            className="relative brutal-border brutal-shadow-lg bg-card rounded-md p-8 sm:p-10 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={close}
              className="absolute top-3 right-3 brutal-border w-8 h-8 flex items-center justify-center bg-background hover:bg-muted rounded-md"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>

            {status === "success" ? (
              <div className="text-center py-4">
                <p
                  role="status"
                  aria-live="polite"
                  className="text-lg font-bold text-foreground leading-relaxed"
                >
                  You're on the list ✓
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  We'll email you the moment it launches.
                </p>
                <button
                  type="button"
                  onClick={close}
                  className="mt-6 brutal-border brutal-shadow bg-primary text-primary-foreground font-bold px-6 py-2 text-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all rounded-md uppercase tracking-[0.08em]"
                >
                  Close
                </button>
              </div>
            ) : (
              <>
                <h3 className="text-2xl sm:text-3xl font-black tracking-tight text-foreground mb-3">
                  Be first when it launches
                </h3>
                <p className="text-muted-foreground mb-6">
                  Drop your email and we'll notify you the moment the book goes live — plus early access pricing.
                </p>
                <form onSubmit={handleSubmit} className="flex flex-col gap-3" noValidate>
                  <label htmlFor="book-cta-email" className="sr-only">
                    Email address
                  </label>
                  <input
                    id="book-cta-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@startup.com"
                    disabled={status === "loading"}
                    className="brutal-border bg-background px-4 py-3 text-sm font-medium text-foreground placeholder:text-muted-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-60"
                  />
                  <button
                    type="submit"
                    disabled={status === "loading"}
                    aria-busy={status === "loading"}
                    className="inline-flex items-center justify-center gap-2 brutal-border brutal-shadow bg-primary text-primary-foreground font-bold px-6 py-3 text-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all rounded-md uppercase tracking-[0.08em] disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {status === "loading" && (
                      <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                    )}
                    {status === "loading" ? "Submitting…" : "Notify Me →"}
                  </button>
                  {status === "error" && (
                    <p role="alert" className="text-sm font-bold text-destructive text-center">
                      Something went wrong. Try again.
                    </p>
                  )}
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default BookCTA;
